<template>
    <form
        ref="root"
        @submit="handleSubmit"
    >
        <client-only>
            <component :is="bodyComponent" />

            <slot />
        </client-only>
    </form>
</template>

<script lang="ts">
import type { DocumentSignatureInput } from '~/resources/graphql/graphql'

export type DocumentSectionFormInput = {
    body: string
    initials: DocumentSignatureInput[]
    signatures: DocumentSignatureInput[]
}
</script>

<script setup lang="ts">
import { ref, computed, h, type PropType, nextTick, watchEffect } from 'vue'
import ReplacementSignature from '~/components/ContentTemplate/ReplacementSignature.vue'
import type { DocumentSignatureMode } from '~/resources/graphql/graphql'
import {
    DocumentSignatureMethod,
    PlanDocumentSignatureMode
} from '../../../../resources/app/generated/enums'

const props = defineProps({
    body: {
        type: String,
        required: true
    },
    signatureMode: {
        type: String as PropType<DocumentSignatureMode>,
        required: true
    },
    savedInitials: {
        type: Array as PropType<DocumentSignatureInput[]>,
        default: () => []
    },
    savedSignatures: {
        type: Array as PropType<DocumentSignatureInput[]>,
        default: () => []
    },
    invalidInitials: {
        type: Set as PropType<Set<number>>,
        required: true
    },
    invalidSignatures: {
        type: Set as PropType<Set<number>>,
        required: true
    }
})

const emit = defineEmits<{
    (
        e: 'documentInput',
        payload: {
            submitter: HTMLElement | null
            documentInput: DocumentSectionFormInput
        }
    ): void
}>()

const root = ref<HTMLFormElement>()
const initialBodyValue: string = props.body
let bodyValue = initialBodyValue
let checkboxCount = 0
while (bodyValue.includes('[[checkbox]]')) {
    checkboxCount += 1
    // Block infinite loops
    if (checkboxCount > 1000) {
        throw new Error()
    }

    const checkboxId = `agree_checkbox_${checkboxCount}`
    bodyValue = bodyValue.replace(
        '[[checkbox]]',
        `<span class="relative inline-block min-w-[25px]">
    <input
        :id="${checkboxId}"
        type="checkbox"
        required
        class="absolute -top-4 left-0"
        style="min-height: 20px; min-width: 20px;"
        :aria-describedby="\`${checkboxId}_error\`"
    />
</span>`
    )
}

const parts = bodyValue.split(
    /(\[\[signature\]\]|\[\[initials\]\]|\[\[checkbox\]\])/g
)

const initials = ref<Array<DocumentSignatureInput | null>>(
    props.savedInitials.length > 0
        ? [...props.savedInitials]
        : parts.filter(value => value === '[[initials]]').map(() => null)
)
const signatures = ref<Array<DocumentSignatureInput | null>>(
    props.savedSignatures.length > 0
        ? [...props.savedSignatures]
        : parts.filter(value => value === '[[signature]]').map(() => null)
)

const effectiveSignatureMode = computed(() => {
    const acceptedMethod =
        signatures.value.find(entry => entry !== null)?.method ??
        initials.value.find(entry => entry !== null)?.method

    if (acceptedMethod === DocumentSignatureMethod.DRAWN) {
        return PlanDocumentSignatureMode.DRAW_ONLY
    }
    if (acceptedMethod === DocumentSignatureMethod.TYPED) {
        return PlanDocumentSignatureMode.TYPE_ONLY
    }
    return props.signatureMode
})

const showErrors = ref(false)
const bodyComponent = computed(() => {
    let initialIndex = -1
    let signatureIndex = -1
    return h(
        'div',
        { class: 'ContentReset' },
        parts.map(value => {
            if (value === '[[initials]]') {
                initialIndex += 1
                const myIndex = initialIndex
                return h(ReplacementSignature, {
                    modelValue: initials.value[myIndex],
                    signatureMode: effectiveSignatureMode.value,
                    initials: true,
                    'onUpdate:modelValue': (
                        payload: DocumentSignatureInput
                    ) => {
                        initials.value[myIndex] = payload
                        props.invalidInitials.delete(myIndex)
                    },
                    showErrors: showErrors.value,
                    invalidData: props.invalidInitials.has(myIndex)
                })
            } else if (value === '[[signature]]') {
                signatureIndex += 1
                const myIndex = signatureIndex
                return h(ReplacementSignature, {
                    modelValue: signatures.value[myIndex],
                    signatureMode: effectiveSignatureMode.value,
                    'onUpdate:modelValue': (
                        payload: DocumentSignatureInput
                    ) => {
                        signatures.value[myIndex] = payload
                        props.invalidSignatures.delete(myIndex)
                    },
                    showErrors: showErrors.value,
                    invalidData: props.invalidSignatures.has(myIndex)
                })
            } else {
                return h('div', { innerHTML: value })
            }
        })
    )
})

function handleSubmit(event: SubmitEvent) {
    event.preventDefault()
    const completedInitials = initials.value.filter(
        (entry): entry is DocumentSignatureInput => entry !== null
    )
    const completedSignatures = signatures.value.filter(
        (entry): entry is DocumentSignatureInput => entry !== null
    )
    if (
        completedInitials.length !== initials.value.length ||
        completedSignatures.length !== signatures.value.length
    ) {
        showErrors.value = true
        nextTick().then(() => {
            root.value?.querySelector('.js-RequiredFormInput')?.scrollIntoView({
                behavior: 'smooth'
            })
        })
        return
    }

    emit('documentInput', {
        submitter: event.submitter,
        documentInput: {
            body: initialBodyValue,
            initials: completedInitials,
            signatures: completedSignatures
        }
    })
}

watchEffect(() => {
    if (props.invalidInitials.size > 0 || props.invalidSignatures.size > 0) {
        nextTick().then(() => {
            root.value?.querySelector('.js-RequiredFormInput')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        })
    }
})
</script>
